import { useGetMonthlyMetrics } from '@actions';
import {
  AcademicCapIcon,
  CheckCircleIcon,
  CurrencyEuroIcon,
  RefreshIcon,
  SwitchHorizontalIcon,
  UserAddIcon,
} from '@heroicons/react/outline';
import { Spin } from 'antd';
import {
  ColumnHeader,
  EmptyState,
  formatEur,
  formatWeekLabel,
  MetricCard,
  SectionHeading,
  TableShell,
} from './common';

const OverviewTab = (props: { month: string; onRescheduleDrilldown: () => void }) => {
  const { month, onRescheduleDrilldown } = props;

  const { data: metricsResponse, isLoading } = useGetMonthlyMetrics({ month });

  const metrics = metricsResponse || {
    mentorAllocations: 0,
    mentorReallocations: 0,
    rescheduledMeetings: 0,
    completedSessions: 0,
    payableSessions: 0,
    strategistSessions: 0,
    totalSessionSpend: 0,
    totalSalarySpend: 0,
    totalSpend: 0,
    averageWeeklySessionSpend: 0,
    averageWeeklyTotalSpend: 0,
    weeks: [],
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-24">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-10">
      <div className="grid grid-cols-1 gap-4 tablet:grid-cols-2 laptop:grid-cols-3">
        <MetricCard
          accent="teal"
          icon={<UserAddIcon className="h-5 w-5" />}
          value={metrics.mentorAllocations}
          label="Mentor allocations"
        />
        <MetricCard
          accent="gold"
          icon={<SwitchHorizontalIcon className="h-5 w-5" />}
          value={metrics.mentorReallocations}
          label="Mentor reallocations"
        />
        <MetricCard
          accent="lila"
          icon={<RefreshIcon className="h-5 w-5" />}
          value={metrics.rescheduledMeetings}
          label="Rescheduled meetings"
          onClick={onRescheduleDrilldown}
        />
        <MetricCard
          accent="green"
          icon={<CheckCircleIcon className="h-5 w-5" />}
          value={metrics.payableSessions}
          label="Payable sessions"
        />
        <MetricCard
          accent="lila"
          icon={<AcademicCapIcon className="h-5 w-5" />}
          value={metrics.strategistSessions}
          label="Education strategist sessions"
        />
        <MetricCard
          accent="gold"
          icon={<CurrencyEuroIcon className="h-5 w-5" />}
          value={formatEur(metrics.averageWeeklyTotalSpend)}
          label="Avg spend / week (incl. salaries)"
        />
      </div>

      <section className="flex flex-col gap-4">
        <SectionHeading
          title="Weekly breakdown"
          description={`Session spend covers only payable sessions, at each mentor's rate. Total spend adds the education strategists' salaries for the month (${formatEur(
            metrics.totalSalarySpend
          )}), split equally across the weeks.`}
        />
        {metrics.weeks.length > 0 ? (
          <TableShell minWidth="min-w-[900px]">
            <thead>
              <tr className="border-b border-white/10 text-xs uppercase tracking-wide text-customGrey">
                <th className="px-5 py-3 text-left font-semibold">Week</th>
                <ColumnHeader dot="teal">Allocations</ColumnHeader>
                <ColumnHeader dot="gold">Reallocations</ColumnHeader>
                <ColumnHeader dot="lila">Reschedules</ColumnHeader>
                <ColumnHeader dot="green">Payable sessions</ColumnHeader>
                <ColumnHeader dot="lila">Strategist sessions</ColumnHeader>
                <ColumnHeader dot="gold">Session spend</ColumnHeader>
                <ColumnHeader dot="gold">Total spend</ColumnHeader>
              </tr>
            </thead>
            <tbody>
              {metrics.weeks.map((week) => (
                <tr
                  key={week.weekStart}
                  className="border-b border-white/5 transition-colors last:border-b-0 hover:bg-surfaceHover"
                >
                  <td className="px-5 py-3 text-left text-customLight">
                    {formatWeekLabel(week.weekStart, week.weekEnd)}
                  </td>
                  <td className="px-5 py-3 text-right tabular-nums">{week.mentorAllocations}</td>
                  <td className="px-5 py-3 text-right tabular-nums">{week.mentorReallocations}</td>
                  <td className="px-5 py-3 text-right tabular-nums">{week.rescheduledMeetings}</td>
                  <td className="px-5 py-3 text-right tabular-nums">{week.payableSessions}</td>
                  <td className="px-5 py-3 text-right tabular-nums">{week.strategistSessions}</td>
                  <td className="px-5 py-3 text-right tabular-nums">
                    {formatEur(week.sessionSpend)}
                  </td>
                  <td className="px-5 py-3 text-right tabular-nums">
                    {formatEur(week.totalSpend)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t border-white/10 font-semibold text-white">
                <td className="px-5 py-3 text-left">Total</td>
                <td className="px-5 py-3 text-right tabular-nums">{metrics.mentorAllocations}</td>
                <td className="px-5 py-3 text-right tabular-nums">{metrics.mentorReallocations}</td>
                <td className="px-5 py-3 text-right tabular-nums">{metrics.rescheduledMeetings}</td>
                <td className="px-5 py-3 text-right tabular-nums">{metrics.payableSessions}</td>
                <td className="px-5 py-3 text-right tabular-nums">{metrics.strategistSessions}</td>
                <td className="px-5 py-3 text-right tabular-nums">
                  {formatEur(metrics.totalSessionSpend)}
                </td>
                <td className="px-5 py-3 text-right tabular-nums">
                  {formatEur(metrics.totalSpend)}
                </td>
              </tr>
            </tfoot>
          </TableShell>
        ) : (
          <EmptyState>No data for this month.</EmptyState>
        )}
      </section>
    </div>
  );
};

export default OverviewTab;
